import { MathUtils } from 'three';
import CommandIdEnum from '../enums/CommandIdEnums';
import IParameterizable from '../interfaces/IParameterizable';
import CommandStack from '../logic/CommandStack';
import ICanvasInputListener from '../logic/ICanvasInputListener';
import Document from '../model/document/Document';
import ISceneObject from '../model/sceneObject/ISceneObject';
import createSceneObject from '../model/sceneObject/SceneObjectFactory';
import SceneObjectData from '../model/sceneObjectData/SceneObjectData';
import { useServicesState } from '../store/servicesStateStore';
import { UUID } from '../utils/typeDefinitions';
import Command from './Command';
import MoveSceneObjectsCmd from './MoveSceneObjectsCmd';
import SelectCmd from './SelectCmd';
import hasRequiredRights from './commandUserRights';

// Duplicates the selected scene objects and hands the copies straight to the move gizmo, so a copy
// is created and placed in one gesture. Each copy is persisted at its original's position and under
// its original's parent, and is kept whatever the move does - finishing without dragging simply
// leaves it sitting on the original.
//
// This is the command on the stack; the MoveSceneObjectsCmd it drives is not (setStacked(false)).
// That is what keeps the running-command bar reading "Clone" throughout: an unstacked command never
// reaches CommandStack.updateCurrentCommand(), so it cannot replace the title, and
// InputCommand.showInfoMessage() ignores it, so it cannot replace the description either. It also
// means a future undo would step back through Clone - the action the user launched - rather than
// through the move that happens to implement part of it.
//
// Deliberately a plain Command rather than a GeometryCommand: this command handles no pointer input
// of its own, and InputCommand's constructor would put a reference-axis and a plane-grid helper into
// the scene while its end() would disconnect the mouse - neither of which makes sense here, and both
// of which would have to be undone in the paths that give up before starting the move. The one thing
// GeometryCommand offers that is wanted is persistEntity, which reduces to #persistClone() below
// once there is no parent object to reparent against.
export default class CloneSceneObjectsCmd extends Command implements IParameterizable<UUID[]> {

    #document: Document;
    #canvasInputListener: ICanvasInputListener;
    #sourceUuids: UUID[] = [];
    #cloneUuids: UUID[] = [];
    #selectCmd: SelectCmd | undefined;
    #move: MoveSceneObjectsCmd | undefined;
    #moveEnded = false;
    #ending = false;

    constructor(commandStack: CommandStack,
                canvasInputListener: ICanvasInputListener,
                document: Document) {
        super(CommandIdEnum.CloneSceneObjects, commandStack);
        this.commandNameResourceKey = 'command.cloneSceneObjects.title';
        this.initialPrompResourceKey = 'command.cloneSceneObjects.prompt';
        this.#document = document;
        this.#canvasInputListener = canvasInputListener;
    }

    setParameters(uuids: UUID[]): void {
        this.#sourceUuids = [...uuids];
    }

    async start(): Promise<void> {
        super.start();

        // Captured before addToStack(), which deactivates whatever is currently on top - after the
        // push the stack top is this command, not SelectCmd.
        const currentCmd = this.getCommandStack().getCurrentCmd();
        if (currentCmd instanceof SelectCmd)
            this.#selectCmd = currentCmd;

        if (!hasRequiredRights(this.#document.userRight, CommandIdEnum.CloneSceneObjects)) {
            console.error('CloneSceneObjectsCmd: user does not have permission to clone scene objects');
            return;
        }

        // Non-cloneable members of a mixed selection are skipped rather than blocking the whole
        // command; only a selection with nothing cloneable in it is refused.
        const sources = this.#sourceUuids
            .map(uuid => this.#document.getSceneObject(uuid))
            .filter((sceneObject): sceneObject is ISceneObject => sceneObject !== undefined)
            .filter(sceneObject => sceneObject.canClone());

        if (sources.length === 0) {
            console.warn('CloneSceneObjectsCmd: nothing in the selection can be cloned');
            return;
        }

        // Nothing above this line touches the stack or the scene, so giving up before here needs no
        // cleanup at all.
        this.addToStack();

        for (const source of sources) {
            // clone() carries the uuid and parentId over, so only the identity has to be replaced.
            // Keeping parentId - and the parent-local coordinates that go with it - is what puts the
            // copy at exactly the original's world position.
            const data = source.data.clone();
            data.uuid = MathUtils.generateUUID();

            const cloneUuid = await this.#persistClone(data);
            if (cloneUuid) this.#cloneUuids.push(cloneUuid);
            else console.error('CloneSceneObjectsCmd: failed to persist cloned scene object');

            // The running-command bar - including its X button - has been up since addToStack(), and
            // commandStop isn't gated by canLaunch, so the command can be ended while this loop is
            // awaiting. Whatever was created is kept and selected; the move is simply never started.
            if (this.#ending) {
                this.#selectClone();
                return;
            }
        }

        if (this.#cloneUuids.length === 0) {
            this.end();
            return;
        }

        this.#selectClone();

        const move = new MoveSceneObjectsCmd(this.getCommandStack(), this.#canvasInputListener, this.#document);
        move.setStacked(false);
        move.setParameters(this.#cloneUuids);
        move.setOnEnd(this.#onMoveEnd);
        // Assigned before start(), which ends the command synchronously if it finds no targets and
        // would then unwind back into this.end().
        this.#move = move;
        move.start();
    }

    // What GeometryCommand.persistEntity does with no parent object set, but handing back the uuid
    // instead of a flag: the object that enters the document is rebuilt from the server's response,
    // so its uuid - not the one generated above - is the one to hold on to.
    async #persistClone(data: SceneObjectData): Promise<UUID | undefined> {
        const result = await useServicesState.getState().dataProvider?.addSceneObject(this.#document.documentUuid, data);
        if (!result) return undefined;

        this.#document.addToDocument(createSceneObject(result));
        return result.uuid;
    }

    #selectClone(): void {
        const uuid = this.#cloneUuids[0];
        if (uuid) this.#selectCmd?.selectSceneObject(uuid);
    }

    #onMoveEnd = () => {
        this.#moveEnded = true;
        this.end();
    };

    end() {
        if (this.#ending) return;
        this.#ending = true;

        // Reached here first when the user pressed the X: CommandManager ends the stack top, which
        // is this command, not the move. Drop the move's callback before ending it so it doesn't
        // come straight back through here.
        if (this.#move && !this.#moveEnded) {
            this.#move.setOnEnd(undefined);
            this.#move.end();
        }

        super.end();
        // SelectCmd.activate() re-selects the copy from the uuid stored by #selectClone().
        this.removeFromStack();
    }
}
